import { buildMultiElementListString } from "utils/copy/buildMultiElementListString"
import InformationModal from "components/views/modal/InformationModal"
import { CenteredLayout } from "components/views/atoms/Layouts"
import { Spacer } from "components/views/atoms/Atoms"
import { Body } from "components/views/atoms/Bodies"
import { CloseButton } from "components/views/atoms/buttons/CloseButton"
import React from "react"
import { FeatureConfiguration } from "types/benefitConfiguration"
import L10n from "lib/carrot-l10n"
import { H2 } from "@carrotfertility/carotene"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

type FeatureFilterableTextItem = {
  text: string
  filter?: (config: FeatureConfiguration) => boolean
}

const informationTypes: FeatureFilterableTextItem[] = [
  { text: "health-related information" },
  {
    text: "information related to your sexual orientation",
    filter: (c) => c.isLgtbqEnabled
  }
]

const dataTypes: FeatureFilterableTextItem[] = [
  { text: "health-related data" },
  {
    text: "data related to your sexual orientation",
    filter: (c) => c.isLgtbqEnabled
  }
]

const ListItem = ({ children }) => <li className="mv3 lh-copy ml3 black-80">{children}</li>

export const DetailedInformationModal = ({ benefitConfiguration, onClose }) => {
  const { features } = benefitConfiguration

  const informationTypeCopyList = buildMultiElementListString(
    informationTypes.filter((t) => !t.filter || t.filter(features)).map((i) => i.text),
    "or"
  ).toLowerCase()

  const dataTypeCopyList = buildMultiElementListString(
    dataTypes.filter((t) => !t.filter || t.filter(features)).map((t) => t.text),
    "or"
  ).toLowerCase()

  return (
    <InformationModal titleText={l`fle2.detailedInformation`} onExit={onClose}>
      <CenteredLayout>
        <div className="mw6">
          <H2 variant="primary" color="black-80">{l`fle2.detailedInformation`}</H2>
          <Spacer height={1} />

          <Body color="black-80">
            Carrot Fertility, Inc., (“Carrot”, ”we”, “us”) collects and processes the following Personal Data from you:
          </Body>

          <Spacer height={0.5} />
          <ul>
            <ListItem>
              any {informationTypeCopyList} that you share with us during the onboarding process or the reimbursement
              process, or which you provide through updates to your profile;
            </ListItem>
            <ListItem>
              any {dataTypeCopyList} that you share with Carrot in the course of any communication we may have; and
            </ListItem>
            <ListItem>
              any health-related information that may be contained in receipts submitted by you to Carrot.
            </ListItem>
          </ul>
          <Spacer height={0.5} />

          <Body color="black-80">
            Carrot collects the Personal Data via written and electronic communication (e.g. when you request
            reimbursement by submitting receipts) and via video, text, and telephone chats. The Carrot Platform uses
            information provided by you to serve you with content and resources that may be relevant to you. In
            addition, the Carrot Care Team uses the Personal Data for the following purposes:
          </Body>
          <Spacer height={1} />

          <Body color="black-80">
            <b>Arranging reimbursements from your employer.</b> Our Care Team uses the Personal Data and receipts you
            submit to us in order to arrange for reimbursement from your employer and to ensure all expenses are for
            covered care.
          </Body>
          <Spacer height={1} />

          <Body color="black-80">
            <b>Validating your eligibility for and use of the Carrot Card®, as applicable.</b> Our Care Team uses your
            Personal Data to validate your Carrot Card® transactions and, in some cases, to determine whether or not you
            are eligible for the Carrot Card® based on eligibility rules set by your employer.
          </Body>
          <Spacer height={1} />

          <Body color="black-80">
            <b>Recommending the most appropriate providers, clinics, agencies, and lawyers, as applicable.</b> Our Care
            Team uses your Personal Data to recommend specific professionals to you.
          </Body>
          <Spacer height={1} />

          <Body color="black-80">
            <b>Providing you with further personalized information and support.</b> Our Care Team uses your Personal
            Data to provide high-quality support tailored to your individual needs. Our Care Team is available to
            respond to any questions you may have about our services.
          </Body>
          <Spacer height={1} />

          <Body color="black-80">
            In addition, Carrot discloses some Personal Data about you to your employer to ensure proper processing of
            reimbursements and, only at your request, to providers when you ask for a self-referral. The information
            disclosed to your employer may include your name and the dollar value of any expenses you have incurred. The
            dollar value may be divided into those expenses that are taxable and those expenses that are not taxable,
            depending on rules set by relevant regulatory bodies and interpreted by your employer. Collectively, this
            information may permit your employer to indirectly draw conclusions about the services you retained. The
            information disclosed to providers is limited to the information you provide to Carrot when you ask for a
            self-referral.
          </Body>
          <Spacer height={1} />

          <Body color="black-80">
            You can withdraw your consent at any time with effect for the future by sending an email to
            info@get-carrot.com. Upon withdrawal of your consent, Carrot will delete your Personal Data or, if
            applicable, store it using additional access restrictions (e.g., if Carrot is obligated by law or audit
            requirements to retain the data for a longer period of time). At this point, Carrot will no longer be able
            to continue providing services to you.
          </Body>
          <Spacer height={1} />

          <Body color="black-80">
            For further information on how we process your data, please see our privacy policy. As Carrot is located in
            California, USA, your data may not be protected by data protection laws equivalent to those in the country
            where you reside, particularly if you reside within the European Union.
          </Body>

          <CloseButton onClose={onClose} showInDesktopView />
        </div>
      </CenteredLayout>
    </InformationModal>
  )
}
